import { WhitelabelConfig, WhitelabelNames } from "@/whitelabel/helpers/types";
import { getCookieByName } from "../Helpers/cookieConsent";
import { isPlatform } from "../Helpers/isPlatform";

// Ensures Google Analytics opt-out works
const initializeGoogleOptOut = (gaProperty: string) => {
  const disableStr = "ga-disable-" + gaProperty;
  if (document.cookie.includes(disableStr + "=true")) {
    window[disableStr] = true;
  }
};

const initializeGTM = (w: Window, d: Document, i: string) => {
  try {
    if (new URLSearchParams(w.location.search).get("nogtm")) {
      return;
    }

    if (w.platformData?.some((item) => item.event === "gtm.js")) {
      console.warn("GTM already initialized, skipping...");
      return;
    }

    w.platformData.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    // Script injection
    const j = d.createElement("script");
    const f = d.getElementsByTagName("script")[0];
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}&l=platformData`;
    f.parentNode?.insertBefore(j, f);
  } catch (error) {
    console.warn("Error initializing GTM:", error);
  }
};

// Initialize Google Publisher Tags (GPT)
const initializeGPT = () => {
  try {
    // Only initialize the command queue, let the GPT script handle the rest
    (window as any).googletag = (window as any).googletag || {};
    (window as any).googletag.cmd = (window as any).googletag.cmd || [];
  } catch (error) {
    console.warn("Error initializing GPT:", error);
  }
};

const initializeTealium = (tealiumUrl: string) => {
  try {
    if (new URLSearchParams(window.location.search).get("notealium")) {
      return;
    }

    if (document.getElementById("tealiumScript")) {
      console.warn("Tealium already initialized, skipping...");
      return;
    }

    window.utag_cfg_ovrd = { noview: true };

    // Simplified script injection
    const script = document.createElement("script");
    script.id = "tealiumScript";
    script.async = true;
    script.src = tealiumUrl;
    document
      .getElementsByTagName("script")[0]
      ?.parentNode?.insertBefore(
        script,
        document.getElementsByTagName("script")[0]
      );
  } catch (error) {
    console.warn("Error initializing Tealium:", error);
  }
};

const initializeConsentManager = (config: {
  brand: string;
  country: string;
  theme: string;
}) => {
  try {
    const utagMainCookie = getCookieByName("utag_main");
    const hasExistingConsent = utagMainCookie?.includes("v_id:");

    if (hasExistingConsent) {
      return;
    }

    if (document.getElementById("cookie-consent-manager")) {
      console.warn("Consent manager already initialized, skipping...");
      return;
    }

    const version = "3";
    const host = "https://assets.staticroot.com/cookie-consent-manager-ui/";
    const scriptUrl = `${host}${version}/cookie-consent-manager.js`;

    const script = document.createElement("script");
    script.id = "cookie-consent-manager";
    script.async = true;
    script.src = scriptUrl;
    script.setAttribute("data-brand", config.brand);
    script.setAttribute("data-country", config.country);
    script.setAttribute("data-lang", "de");
    script.setAttribute("data-theme", config.theme);
    script.setAttribute("data-host", host);
    script.setAttribute("data-version", version);
    document
      .getElementsByTagName("script")[0]
      .parentNode?.insertBefore(
        script,
        document.getElementsByTagName("script")[0]
      );
  } catch (error) {
    console.warn("Error initializing consent manager:", error);
  }
};

const initializePlatformData = () => {
  if (typeof window === "undefined") return;

  const originalPush = Array.prototype.push;
  window.platformData.push = function (...args) {
    const trackingObject = args[0] as { event: string };

    if (
      window.ReactNativeWebView &&
      ["visit", "conversion"].includes(trackingObject.event)
    ) {
      const payload = {
        eventType: "tourOperator",
        platformData: [trackingObject],
      };
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    }

    return originalPush.apply(this, args);
  };
};

type InitializeTrackingParams = {
  gtmId?: string;
  tealiumUrl?: { prod: string; qa: string };
  currentEnv?: string;
  cookieConsentManager?: {
    brand: string;
    country: string;
    theme: string;
  };
  gaProperty?: string;
  WlConfig?: WhitelabelConfig;
};

export const initializeTracking = ({
  gtmId,
  tealiumUrl,
  currentEnv,
  cookieConsentManager,
  gaProperty,
  WlConfig,
}: InitializeTrackingParams) => {
  if (typeof window === "undefined") return;

  // Initialize platformData array first
  window.platformData = window.platformData || [];

  // Set GDPR flag (from legacy code)
  window.GDPR = true;

  // Check if we're on homepage
  const isHomePage = window.location.pathname === "/";

  // Get the correct GTM ID based on page type
  let finalGtmId = gtmId;
  if (isHomePage && WlConfig?.platformDataTracking?.startPageId) {
    finalGtmId = WlConfig?.platformDataTracking?.startPageId;
  }

  // Check if we're in app using the legacy platform detection
  const isApp = isPlatform();

  // Initialize platform data functionality EARLY for mobile apps (needed for mobile app bridge)
  // For web, this will be called later to preserve original initialization order
  if (isApp) {
    initializePlatformData();
  }

  // Initialize Google opt-out if gaProperty provided
  if (gaProperty) {
    initializeGoogleOptOut(gaProperty);
  }

  // Initialize GTM with correct ID
  if (finalGtmId && WlConfig?.name === WhitelabelNames.wegde) {
    initializeGTM(window, document, finalGtmId);
  }

  // Initialize Google Publisher Tags (GPT)
  initializeGPT();

  // Initialize Tealium
  if (tealiumUrl) {
    const finalTealiumUrl =
      currentEnv === "production" ? tealiumUrl.prod : tealiumUrl.qa;
    initializeTealium(finalTealiumUrl);
  }

  // Initialize consent manager ONLY for web (skip for app - matches original)
  if (cookieConsentManager && !isApp) {
    initializeConsentManager(cookieConsentManager);
  }

  // Initialize platform data functionality for web (after all tracking tools are set up)
  // For mobile apps, this was already called earlier
  if (!isApp) {
    initializePlatformData();
  }
};
